import type { ConsentManagerOptions } from '@c15t/react';
import type { Callbacks, Script } from 'c15t';

import { CATEGORIES } from './categories.js';

/**
 * The slice of `t.consent.*` that this factory needs. Kept narrow so a test
 * can pass a stub without importing the SPA's full translation tree.
 *
 * Mount sites do: `buildConsentConfig({ translations: t.consent, ... })`.
 */
export type ConsentTranslations = {
  banner: {
    title: string;
    description: string;
    acceptAll: string;
    rejectAll: string;
    customize: string;
  };
  dialog: {
    title: string;
    description: string;
    save: string;
  };
  categories: Record<
    'necessary' | 'measurement' | 'marketing',
    { title: string; description: string }
  >;
};

export type BuildConsentConfigInput = {
  translations: ConsentTranslations;
  /** c15t-scripts integrations (e.g. PostHog) — wired by downstream features. */
  extraScripts?: ReadonlyArray<Script>;
  /**
   * c15t lifecycle callbacks. Downstream features ([`posthog-integration`](../../../docs/features/posthog-integration/design.md))
   * hook into `onConsentChanged` to sync third-party SDK opt-in/out — that callback
   * fires only on explicit user save (true transitions), not on every page hydration,
   * which is why the consent-sync logic lives there instead of in a React effect.
   */
  callbacks?: Callbacks | undefined;
};

export function buildConsentConfig({
  translations,
  extraScripts = [],
  callbacks,
}: BuildConsentConfigInput): ConsentManagerOptions {
  return {
    mode: 'offline',
    consentCategories: [...CATEGORIES],
    scripts: [...extraScripts],
    networkBlocker: { enabled: false, rules: [] },
    ...(callbacks ? { callbacks } : {}),
    i18n: {
      locale: 'nl',
      detectBrowserLanguage: false,
      messages: {
        nl: {
          common: {
            acceptAll: translations.banner.acceptAll,
            rejectAll: translations.banner.rejectAll,
            customize: translations.banner.customize,
            save: translations.dialog.save,
          },
          cookieBanner: {
            title: translations.banner.title,
            description: translations.banner.description,
          },
          consentManagerDialog: {
            title: translations.dialog.title,
            description: translations.dialog.description,
          },
          consentTypes: {
            necessary: translations.categories.necessary,
            measurement: translations.categories.measurement,
            marketing: translations.categories.marketing,
          },
        },
      },
    },
  };
}
